import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Trash2, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ClearCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ClearCalendarModal: React.FC<ClearCalendarModalProps> = ({ 
  isOpen, 
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleClearCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('User not authenticated');
      
      // Delete all calendar events for this user
      const { error: deleteError } = await supabase
        .from('calendar_events')
        .delete()
        .eq('user_id', user.id);
        
      if (deleteError) throw deleteError;
      
      setSuccess(true);
      
      // Reset form and close after delay
      setTimeout(() => {
        setConfirmText('');
        onSuccess();
        onClose();
      }, 1500);
      
    } catch (err) {
      console.error('Error clearing calendar:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear calendar');
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setConfirmText('');
    setError(null);
    setSuccess(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={() => {
              resetState();
              onClose();
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Clear Calendar
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      resetState();
                      onClose();
                    }}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                    <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                  </div>
                )}

                {success ? (
                  <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                    <p className="text-sm text-green-600 dark:text-green-300">
                      Calendar successfully cleared!
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6">
                      <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                        Warning: This action cannot be undone
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-200 mt-2">
                        This will permanently delete all events from your calendar. You will not be able to recover this data.
                      </p>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Type "clear" to confirm
                      </label>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                        placeholder="clear"
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          resetState();
                          onClose();
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleClearCalendar}
                        disabled={loading || confirmText !== 'clear'}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          confirmText === 'clear' && !loading
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {loading ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Clearing...
                          </span>
                        ) : (
                          'Clear Calendar'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ClearCalendarModal;